import { StaffPositionEnum } from '../../enums/staff/staff-position.enum'
import { StaffQualificationEnum } from '../../enums/staff/staff-qualifications.enum'
import { TransportTypeEnum } from '../../enums/trip/trip-type.enum'
import { TripStepTypeEnum } from '../../enums/trip-bookings/trip-step-type.enum'
import { Time } from '../../utils'
import { TripAddressLight, TripAddressPartial } from '../trip-address/trip-address'
import { TripBookingTypes } from '../trip-booking/trip-booking'
import {
  TripBookingDispatchPlaceIndividualTypeEnum,
  TripDateTripManagerMapping
} from '../trip-booking/trip-date'
import { TripStepTypes } from '../trip-booking/trip-step'
import {
  TripBookingDispatchPlace,
  TripBookingDispatchPlaceLight,
  TripBookingDispatchPlacePartial
} from './trip-booking-dispatch-place'

export type DispatchStaffInformation = {
  id: number
  firstName: string
  lastName: string
  position: StaffPositionEnum
  qualifications: StaffQualificationEnum[]
  phoneNumber: string
  departureCity: string
  center: string
  staffId?: number
}

export type Appointment<
  T extends TripBookingDispatchPlaceLight | TripBookingDispatchPlacePartial
> = T extends TripBookingDispatchPlacePartial
  ? { time: Time; place: TripAddressPartial }
  : { time?: Time; place?: TripAddressLight }

export class TripBookingDispatchPlaceProperties {
  static getStaffInformation(dp: TripBookingDispatchPlace): DispatchStaffInformation {
    switch (dp.individualType) {
      case TripBookingDispatchPlaceIndividualTypeEnum.AFFECTED_STAFF:
        return {
          id: dp.affectedStaffId,
          staffId: dp.affectedStaff.staff.id,
          firstName: dp.affectedStaff.staff.firstName ?? '',
          lastName: dp.affectedStaff.staff.lastName ?? '',
          position: dp.affectedStaff.staff.position ?? StaffPositionEnum.AAT,
          qualifications: dp.affectedStaff.staff.qualifications ?? [],
          phoneNumber: dp.affectedStaff.staff.phoneNumber ?? '',
          departureCity: '',
          center: ''
        }

      case TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_EXTERNAL_STAFF:
        return {
          id: dp.missionToExternalStaffId,
          firstName: dp.missionToExternalStaff.firstName,
          lastName: dp.missionToExternalStaff.lastName,
          position: StaffPositionEnum.AAT,
          qualifications: dp.missionToExternalStaff.mission.qualifications ?? [],
          phoneNumber: dp.missionToExternalStaff.phone ?? '',
          departureCity: '',
          center: ''
        }
      case TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_STAFF:
        return {
          id: dp.missionToStaffId,
          firstName: dp.missionToStaff.staff.firstName ?? '',
          lastName: dp.missionToStaff.staff.lastName ?? '',
          position: dp.missionToStaff.staff.position ?? StaffPositionEnum.AAT,
          qualifications: dp.missionToStaff.staff.qualifications ?? [],
          phoneNumber: dp.missionToStaff.staff.phoneNumber ?? '',
          departureCity: '',
          center: ''
        }
      case TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_LEC_STAFF:
        return {
          id: dp.missionToLecStaffId,
          firstName: dp.missionToLecStaff.user.firstName ?? '',
          lastName: dp.missionToLecStaff.user.firstName ?? '',
          position: StaffPositionEnum.AAT,
          qualifications: dp.missionToLecStaff.mission.qualifications ?? [],
          phoneNumber: '',
          departureCity: '',
          center: ''
        }

      case TripBookingDispatchPlaceIndividualTypeEnum.ENROLLMENT:
        throw new Error('getStaffInformation should not be called for ENROLLMENT type')
    }
  }

  /**
   * Qualification(s) the individual was actually hired for, as opposed to every qualification
   * their staff profile could hold:
   * - an affected staff is hired through a staff request, which carries a single qualification
   * - a mission staff is hired for the qualifications of the mission itself
   */
  static getHiredQualifications(dp: TripBookingDispatchPlace): StaffQualificationEnum[] {
    switch (dp.individualType) {
      case TripBookingDispatchPlaceIndividualTypeEnum.AFFECTED_STAFF:
        return dp.affectedStaff.staffRequest.qualification
          ? [dp.affectedStaff.staffRequest.qualification]
          : []

      case TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_EXTERNAL_STAFF:
        return dp.missionToExternalStaff.mission.qualifications ?? []

      case TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_STAFF:
        return dp.missionToStaff.mission.qualifications ?? []

      case TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_LEC_STAFF:
        return dp.missionToLecStaff.mission.qualifications ?? []

      case TripBookingDispatchPlaceIndividualTypeEnum.ENROLLMENT:
        throw new Error('getHiredQualifications should not be called for ENROLLMENT type')
    }
  }

  static getStaffDeparturePlace(dp: TripBookingDispatchPlace) {
    switch (dp.individualType) {
      case TripBookingDispatchPlaceIndividualTypeEnum.AFFECTED_STAFF:
        return dp.affectedStaff.trip?.departure

      case TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_EXTERNAL_STAFF:
        if (dp.missionToExternalStaff.mission.tripBookings.length === 0) return
        return dp.missionToExternalStaff.mission.tripBookings[0].relatedTrip.departure

      case TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_STAFF:
        if (dp.missionToStaff.mission.tripBookings.length === 0) return
        return dp.missionToStaff.mission.tripBookings[0].relatedTrip.departure

      case TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_LEC_STAFF:
        if (dp.missionToLecStaff.mission.tripBookings.length === 0) return
        return dp.missionToLecStaff.mission.tripBookings[0].relatedTrip.departure

      case TripBookingDispatchPlaceIndividualTypeEnum.ENROLLMENT:
        throw new Error('getStaffInformation should not be called for ENROLLMENT type')
    }
  }

  static getStaffCenter(dp: TripBookingDispatchPlace) {
    switch (dp.individualType) {
      case TripBookingDispatchPlaceIndividualTypeEnum.AFFECTED_STAFF:
        return dp.affectedStaff.staffRequest.centers

      case TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_EXTERNAL_STAFF:
      case TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_STAFF:
      case TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_LEC_STAFF:
      case TripBookingDispatchPlaceIndividualTypeEnum.ENROLLMENT:
        throw new Error('getStaffCenter should only be called for AFFECTED_STAFF type')
    }
  }

  static hasResponsibility(
    dp: TripBookingDispatchPlace,
    tripManagers: TripDateTripManagerMapping
  ) {
    if (dp.individualType !== TripBookingDispatchPlaceIndividualTypeEnum.AFFECTED_STAFF)
      throw new Error('hasResponsibility should not be called for ENROLLMENT type')

    const centerIds = TripBookingDispatchPlaceProperties.getStaffCenter(dp)?.map((c) => c.id)
    if (!centerIds) return false

    const hasResponsibility = centerIds.some((centerId) => {
      const tripManager = tripManagers[centerId]
      return tripManager?.id === TripBookingDispatchPlaceProperties.getStaffInformation(dp).id
    })

    return hasResponsibility
  }

  static getStaffMissions(dp: TripBookingDispatchPlace) {
    switch (dp.individualType) {
      case TripBookingDispatchPlaceIndividualTypeEnum.AFFECTED_STAFF:
        return undefined

      case TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_EXTERNAL_STAFF:
        return dp.missionToExternalStaff.mission

      case TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_STAFF:
        return dp.missionToStaff.mission

      case TripBookingDispatchPlaceIndividualTypeEnum.MISSION_TO_LEC_STAFF:
        return dp.missionToLecStaff.mission

      case TripBookingDispatchPlaceIndividualTypeEnum.ENROLLMENT:
        throw new Error('getStaffMissions should not be called for ENROLLMENT type')
    }
  }

  static getAppointment<T extends TripBookingDispatchPlaceLight | TripBookingDispatchPlacePartial>(
    dp: T
  ): Appointment<T> {
    if (dp.tripBooking?.isTakeInCharge)
      return {
        time: dp.tripBooking?.meetingTimeForStaff,
        place: dp.tripBooking?.meetingPlace
      } as Appointment<T>

    if (dp.directionality === TripStepTypeEnum.INBOUND) {
      const time = dp.tripStep?.timeBeforeDepartureForStaff
      const place = dp.tripStep?.departurePlace
      return { time, place } as Appointment<T>
    }

    return {
      time: dp.tripStep?.timeBeforeDepartureForGroup,
      place: dp.tripStep?.departurePlace
    } as Appointment<T>
  }

  /**
   * Get the place where the parents meet the group: the departure place on the way out,
   * the arrival place on the way back
   * @param dp - The dispatch place
   * @returns The parents meeting place
   */
  static getParentsMeetingPlace(
    dp: Pick<TripBookingDispatchPlace, 'directionality'> & {
      tripStep?: Pick<TripStepTypes.Compact, 'departurePlace' | 'arrivalPlace'>
    }
  ) {
    return dp.directionality === TripStepTypeEnum.INBOUND
      ? dp.tripStep?.departurePlace
      : dp.tripStep?.arrivalPlace
  }

  /**
   * Get the trip info for a dispatch place
   * @param dp - The dispatch place
   * @returns The trip info
   */
  static getTripInfo(
    dp: Pick<TripBookingDispatchPlace, 'directionality'> & {
      tripBooking?: Pick<TripBookingTypes.Slim, 'isTakeInCharge'> & {
        travelCompany?: Pick<NonNullable<TripBookingTypes.Slim['travelCompany']>, 'name'>
      }
      tripStep?: Pick<
        TripStepTypes.Compact,
        | 'departureDate'
        | 'arrivalDate'
        | 'timeBeforeDepartureForParents'
        | 'timeBeforeArrivalForParents'
        | 'departureTimeZone'
        | 'arrivalTimeZone'
        | 'transportMeanNumber'
        | 'carNumber'
      >
    }
  ) {
    const { directionality, tripBooking, tripStep } = dp

    const transportType =
      directionality === TripStepTypeEnum.INBOUND
        ? TransportTypeEnum.ONE_WAY
        : TransportTypeEnum.RETURN

    const date =
      directionality === TripStepTypeEnum.INBOUND ? tripStep?.departureDate : tripStep?.arrivalDate

    const time =
      directionality === TripStepTypeEnum.INBOUND
        ? tripStep?.timeBeforeDepartureForParents
        : tripStep?.timeBeforeArrivalForParents

    const timezone =
      directionality === TripStepTypeEnum.INBOUND
        ? tripStep?.departureTimeZone
        : tripStep?.arrivalTimeZone

    const company = tripBooking?.travelCompany?.name
    const transportMeanNumber = tripStep?.transportMeanNumber
    const carNumber = tripStep?.carNumber

    return { date, time, timezone, transportType, company, transportMeanNumber, carNumber }
  }
}
